import { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useLazyQuery } from '@apollo/react-hooks'
import * as Yup from 'yup'

import Form from '~/components/shared/Form'
import { LogIn, GetCurrentUser } from '~/graphql/Users.gql'
import withLocale from '~/hocs/locale'
import { Link } from '~/i18n'
import * as auth from '~/utils/auth'
import '~/utils/extended-validation'

const INITIAL_VALUES = {
  email: '',
  password: '',
}

const VALIDATION = Yup.object({
  email: Yup.string().trim().email().required()
    .label('email'),
  password: Yup.string().trim().required().label('password'),
})

const Login = ({
  passwordLabel,
  ctaLabel,
  forgotMyPassword,
  invalidUsernamePasswordLabel,
  onSubmit,
}) => {
  const handleOnGetUser = data => {
    auth.storeCurrentUser(data.currentUser)
    onSubmit()
  }

  const handleOnError = error => {
    auth.clearToken()
    setResponse({ error })
  }

  const handleSubmit = async (values, { setErrors }) => {
    const { data } = await logIn({ variables: values })
    const { jwt } = data.authenticate
    if (jwt) {
      auth.storeToken(jwt)
      getCurrentUser()
    } else {
      setErrors({
        email: invalidUsernamePasswordLabel,
        password: invalidUsernamePasswordLabel,
      })
    }
  }

  const [logIn] = useMutation(LogIn, { onError: handleOnError })
  const [getCurrentUser] = useLazyQuery(GetCurrentUser, {
    onCompleted: handleOnGetUser,
    onError: handleOnError,
    fetchPolicy: 'no-cache',
  })
  const [response, setResponse] = useState()

  return (
    <Form
      onSubmit={handleSubmit}
      response={response}
      initialValues={INITIAL_VALUES}
      validationSchema={VALIDATION}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <Form.Field name="email" label="Email" />
      <Form.Field name="password" type="password" label={passwordLabel} />
      <div className="text-center">
        <Form.Submit
          text={ctaLabel}
          submittingText="Logging in"
          size="lg"
          className="mt-3"
          type="submit"
        />
      </div>
      <small className="d-flex justify-content-center mt-4">
        <Link href={auth.RESET_PASSWORD_PATH} passHref prefetch={false}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a>{forgotMyPassword}</a>
        </Link>
      </small>
    </Form>
  )
}

Login.propTypes = {
  passwordLabel: PropTypes.string.isRequired,
  ctaLabel: PropTypes.string.isRequired,
  forgotMyPassword: PropTypes.string.isRequired,
  invalidUsernamePasswordLabel: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default withLocale('common', 'login')(Login)
